import { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { LANGS } from '../utils/siteNav';
import { OG_LOCALES, absUrl } from '../seo/hotelFacts';
import { pageMeta } from '../seo/pageMeta';
import { buildGraph } from '../seo/buildSchema';
import { useRooms } from '../hooks/useRooms';
import { optimized } from '../utils/img';

const HREFLANG = { me: 'sr' };

const setMeta = (selector, content) => {
  const el = document.querySelector(selector);
  if (el) el.setAttribute('content', content);
};

// Localised <head> for the public site: title, description, OG/Twitter, canonical + hreflang and a per-page schema.org graph.
// `meta` overrides title/description/image for pages outside pageMeta (room, guide, legal); `schema` extends the JSON-LD graph.
const SEOHead = ({ page = '', meta, schema }) => {
  const { language, t } = useLanguage();
  const rooms = useRooms();

  useEffect(() => {
    const base = pageMeta(language, meta ? '' : page, t);
    const title = meta?.title || base.title;
    const description = meta?.description || base.description;
    const canonicalUrl = absUrl(language, page);

    document.title = title;
    document.documentElement.lang = language === 'me' ? 'sr' : language;
    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:url"]', canonicalUrl);
    setMeta('meta[property="og:type"]', meta?.ogType || 'website');
    setMeta('meta[property="og:locale"]', OG_LOCALES[language] || OG_LOCALES.en);
    setMeta('meta[property="twitter:title"]', title);
    setMeta('meta[property="twitter:description"]', description);
    setMeta('meta[property="twitter:url"]', canonicalUrl);
    const heroImage = meta?.image || (rooms.find((r) => /premium/i.test(r.name)) || rooms[0])?.images?.[0];
    if (heroImage) {
      setMeta('meta[property="og:image"]', optimized(heroImage, 1200));
      setMeta('meta[property="twitter:image"]', optimized(heroImage, 1200));
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', canonicalUrl);
    LANGS.forEach((lang) => {
      const el = document.querySelector(`link[rel="alternate"][hreflang="${HREFLANG[lang] || lang}"]`);
      if (el) el.setAttribute('href', absUrl(lang, page));
    });
    const xd = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
    if (xd) xd.setAttribute('href', absUrl('en', page));

    let script = document.getElementById('seo-jsonld');
    if (!script) {
      script = document.createElement('script');
      script.id = 'seo-jsonld';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    const graph = buildGraph({ language, slug: page, url: canonicalUrl, title, description, t, rooms, extra: schema });
    script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
  }, [language, t, page, rooms, meta, schema]);

  return null;
};

export default SEOHead;
